import type { WorkRequestStepOut } from "../../../.generated/api-client/models/WorkRequestStepOut";
import type { VisualLedgerTone } from "../../components/visual-ledger/VisualLedger";
import type {
  AmendmentApprovalForm,
  ApprovalStatusFilter,
  MyApprovalStatusFilter,
  ProductItemValues,
  RequestType,
} from "./types";

export const REASON_CN: Record<string, string> = {
  forgot: "忘打卡",
  phone_dead: "手机没电",
  gps_anomaly: "GPS 异常",
  system_failure: "系统故障",
  other: "其他",
};

export const STATUS_CN: Record<string, string> = {
  pending: "待处理",
  in_review: "审批中",
  approved: "已通过",
  rejected: "已驳回",
  returned: "已退回",
  withdrawn: "已撤回",
  cancelled: "已作废",
  expired: "已失效",
};

export const STATUS_FILTER_EMPTY_TEXT: Record<ApprovalStatusFilter, string> = {
  approved: "暂无已通过申请",
  rejected: "暂无已驳回申请",
  returned: "暂无已退回申请",
};

/**
 * 后端 from > to 的错误码 —— 后端**不静默交换**日期, 直接 422。
 * 前端用 RangePicker 结构上就产不出 from > to, 这里是兜底: 万一命中, 说人话而不是甩错误码。
 */
export const INVALID_DATE_RANGE_CODE = "WORK_REQUEST_INVALID_DATE_RANGE";
export const INVALID_DATE_RANGE_MESSAGE =
  "开始日期不能晚于结束日期，请重新选择时间段。";

export const REQUEST_TYPE_CN: Record<string, string> = {
  amendment: "补卡",
  leave: "请假/月休",
  field_work: "历史带薪外勤",
  field_punch: "外勤申请",
  supplier_onboarding: "供应商准入",
  supplier_product_add: "供应商加品",
  reimbursement: "报销",
  employee_onboarding: "入职审批",
  offboarding: "离职审批",
  schedule_change: "排班调整",
};

export const REQUEST_TYPE_OPTIONS: Array<{
  label: string;
  value: RequestType;
}> = [
  { label: "全部类型", value: "all" },
  { label: "补卡", value: "amendment" },
  { label: "请假/月休", value: "leave" },
  { label: "历史带薪外勤", value: "field_work" },
  { label: "外勤申请", value: "field_punch" },
  { label: "供应商准入", value: "supplier_onboarding" },
  { label: "供应商加品", value: "supplier_product_add" },
  { label: "报销", value: "reimbursement" },
  { label: "入职审批", value: "employee_onboarding" },
  { label: "离职审批", value: "offboarding" },
  { label: "排班调整", value: "schedule_change" },
];

export const UPPER_SCOPE_REQUEST_TYPE_OPTIONS = REQUEST_TYPE_OPTIONS.filter(
  (option) =>
    [
      "all",
      "leave",
      "supplier_onboarding",
      "supplier_product_add",
      "reimbursement",
      "schedule_change",
    ].includes(option.value),
);

export const FINANCE_REVIEW_REQUEST_TYPE_OPTIONS = REQUEST_TYPE_OPTIONS.filter(
  (option) =>
    ["all", "reimbursement", "supplier_onboarding"].includes(option.value),
);

export const MY_STATUS_OPTIONS: Array<{
  label: string;
  value: MyApprovalStatusFilter;
}> = [
  { label: "全部状态", value: "all" },
  { label: "待处理", value: "pending" },
  { label: "审批中", value: "in_review" },
  { label: "已通过", value: "approved" },
  { label: "已驳回", value: "rejected" },
  { label: "已退回", value: "returned" },
  { label: "已撤回", value: "withdrawn" },
  { label: "已失效", value: "expired" },
  { label: "已作废", value: "cancelled" },
];

export const PROCESSED_STATUS_OPTIONS: Array<{
  label: string;
  value: ApprovalStatusFilter;
}> = [
  { label: "已通过", value: "approved" },
  { label: "已驳回", value: "rejected" },
  { label: "已退回", value: "returned" },
];

/**
 * 「审批参考」四格统计的口径 = 补卡次数 (后端 risk_signals 只给这一种)。
 * 只有补卡审批会用这组数判断是否频繁漏卡；请假、外勤以及其它类型均不放行。
 */
export const AMENDMENT_REFERENCE_TYPES: ReadonlySet<string> = new Set([
  "amendment",
]);

export const ROLE_CN: Record<string, string> = {
  staff: "员工",
  store_manager: "店长",
  area_manager: "区域负责人",
  finance: "财务",
  owner: "老板",
  super_admin: "超级管理者",
};

export const STEP_STATUS_CN: Record<WorkRequestStepOut["status"], string> = {
  pending: "待流转",
  active: "当前审批",
  approved: "已通过",
  rejected: "已驳回",
  returned: "已退回",
  skipped: "已跳过",
  cancelled: "已取消",
};

export const STEP_STATUS_COLOR: Record<
  WorkRequestStepOut["status"],
  VisualLedgerTone
> = {
  pending: "neutral", // 待流转
  active: "info", // 当前审批 (processing → 进行中)
  approved: "success", // 已通过
  rejected: "danger", // 已驳回
  returned: "warning", // 已退回 → 待补充
  skipped: "muted", // 已跳过 → 失效
  cancelled: "muted", // 已取消 → 作废
};

export const PRODUCT_ITEM_INITIAL: ProductItemValues = {
  itemName: "",
  categoryId: undefined as unknown as number,
  spec: "",
  defaultUnit: "",
  looseUnit: "",
  conversionFactor: undefined as unknown as number,
};

export const INVOICE_OPTIONS = [
  { label: "专票", value: "special_vat" },
  { label: "普票", value: "general_vat" },
  { label: "无票", value: "none" },
];

export const AMENDMENT_MODE_OPTIONS: Array<{
  label: string;
  value: AmendmentApprovalForm["punchMode"];
}> = [
  { label: "上下班双补", value: "both" },
  { label: "仅补上班", value: "in" },
  { label: "仅补下班", value: "out" },
];

export const AMENDMENT_REASON_OPTIONS: Array<{
  label: string;
  value: AmendmentApprovalForm["reasonEnum"];
}> = [
  { label: "忘打卡", value: "forgot" },
  { label: "手机没电", value: "phone_dead" },
  { label: "GPS 异常", value: "gps_anomaly" },
  { label: "系统故障", value: "system_failure" },
  { label: "其他", value: "other" },
];
